import { toPathString, useForm } from "@/lib/form/form"
import { useCallback, useEffect } from "react"
import { Schema, schemaPathToValuePath } from "@/rmpm/lib/schema"
import { ParamValidationType } from "@/lib/form/schema"
import { createLimitValidator } from "@/lib/validators/limit"
import { createChoiceValidator } from "@/lib/validators/choice"
import { FormParamStatus } from "@/lib/form/types"

export function useRmPmForm(schema?: Schema) {
  const form = useForm()

  const registerSchema = useCallback(
    async (schema: Schema, path = "") => {

      const defaultValue = schema.properties?.value?.default || ""
      const status = schema.options?.disabled ? FormParamStatus.DISABLED : FormParamStatus.DRAFT
      const now = new Date().toISOString()
      const isRequired = schema.properties?.value?.required === true;
      const isLabel = schema.options?.label;
      const productId = schema?.productId;
      const parameterId = schema?.parameterId;

      if (schema.options?.paramSchema) {
        const paramSchema = schema.options.paramSchema

        if (paramSchema.validationType === ParamValidationType.LIMIT) {
          form.register(schemaPathToValuePath(path), {
            shouldTrigger: false,
            value: {
              id: schema.properties?.id.default,
              recordedAt: now,
              value: defaultValue,
              status: status,
              required: isRequired,
              ...(isLabel !== undefined ? { label: isLabel } : {}),
              ...(productId !== undefined ? { productId: productId } : {}),
              ...(parameterId !== undefined ? { parameterId: parameterId } : {}),
            },
            validate: createLimitValidator({
              schema: paramSchema,
              path: schemaPathToValuePath(path),
              form,
              required: true,
            }),
          })
        } else if (paramSchema.validationType === ParamValidationType.CHOICE) {
          form.register(schemaPathToValuePath(path), {
            shouldTrigger: false,
            value: {
              id: schema.properties?.id.default,
              ...(parameterId !== undefined ? { parameterId: parameterId } : {}),
              ...(productId !== undefined ? { productId: productId } : {}),
              recordedAt: now,
              value: defaultValue,
              status: status,
              required: isRequired,
              ...(isLabel !== undefined ? { label: isLabel } : {}),
            },
            validate: createChoiceValidator({
              schema: paramSchema,
              path: schemaPathToValuePath(path),
              form,
              required: true,
            }),
          })
        } else {
          form.register(schemaPathToValuePath(path), {
            shouldTrigger: false,
            value: {
              id: schema.properties?.id.default,
              ...(parameterId !== undefined ? { parameterId: parameterId } : {}),
              ...(productId !== undefined ? { productId: productId } : {}),
              recordedAt: now,
              value: defaultValue,
              status: status,
              required: isRequired,
              ...(isLabel !== undefined ? { label: isLabel } : {}),
            },
          })
        }
      } else if (schema.options?.widget) {
        form.register(schemaPathToValuePath(path), {
          shouldTrigger: false,
          value: {
            id: schema.properties?.id?.default,
            ...(parameterId !== undefined ? { parameterId: parameterId } : {}),
            ...(productId !== undefined ? { productId: productId } : {}),
            recordedAt: now,
            value: defaultValue,
            status: status,
            required: isRequired,
            ...(isLabel !== undefined ? { label: isLabel } : {}),
          },
        })
      }

      if (schema.properties) {
        for (const field in schema.properties) {
          registerSchema(
            schema.properties[field],
            toPathString([path, "properties", field])
          )
        }
      } else if (schema.items) {
        for (const [index, item] of schema.items.entries()) {
          registerSchema(item, toPathString([path, "items", `${index}`]))
        }
      }
    },
    [form]
  )

  const rebuild = useCallback(
    async (schema: Schema) => {
      form.unregister({
        keepHandlers: true,
      });

      form.register("sheetId", {
        value: schema.sheetId,
        shouldTrigger: false,
      });

      await registerSchema(schema, "");

      await form.runAllValidators();
    },
    [form, registerSchema]
  );


  useEffect(() => {
    if (schema) {
      rebuild(schema)
    }
  }, [form])

  return {
    form,
    rebuild,
  }
}
